/**
 * The investigation report for one run, in a dialog: the judge stays in the thread, while the
 * investigation, a fault report about the pipeline, gets the width to be read here. Two panels
 * because the envelope answers two questions: what broke, and why believe it.
 */

import { useState } from 'react';
import { ClipboardList, Microscope } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Segmented, type SegmentedOption } from '@/modules/simulator/ui/segmented';
import { humanize } from '@/modules/simulator/ui/text';
import { readVerdictReport } from '../report-data';
import { DiagnosisPanel } from './diagnosis-panel';
import { EvidencePanel } from './evidence-panel';
import { Chip, SeverityDots } from './report-bits';

type Tab = 'diagnosis' | 'evidence';

const TABS: SegmentedOption<Tab>[] = [
  { value: 'diagnosis', label: 'Diagnosis', icon: Microscope },
  { value: 'evidence', label: 'Evidence', icon: ClipboardList },
];

interface RunReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The investigator's envelope. The dialog is only offered once there is one. */
  investigation: unknown;
}

export const RunReportDialog = ({ open, onOpenChange, investigation }: RunReportDialogProps) => {
  const report = readVerdictReport(investigation);
  const [tab, setTab] = useState<Tab>('diagnosis');
  if (!report) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-5xl gap-0 overflow-hidden p-0'>
        <header className='flex flex-col gap-3.5 border-b border-gray-500 px-6 pt-6 pb-4'>
          <div className='flex items-start justify-between gap-6 pr-8'>
            <div className='flex min-w-0 flex-col gap-2'>
              <DialogTitle className='text-[15px]'>Investigation report</DialogTitle>
              <DialogDescription className='sr-only'>
                What the investigator found in this run&apos;s trace, and the evidence behind it.
              </DialogDescription>
              <div className='flex flex-wrap items-center gap-2'>
                {report.disposition && (
                  <Chip tone={report.disposition === 'fail' ? 'solid' : 'outline'}>{humanize(report.disposition)}</Chip>
                )}
                {report.severity && <SeverityDots severity={report.severity} />}
                {report.fixKey && <Chip mono>{report.fixKey}</Chip>}
              </div>
            </div>
          </div>
          <Segmented options={TABS} value={tab} onChange={setTab} className='w-fit' />
        </header>

        {/* Keyed on the panel so switching replays the stagger. */}
        <div key={tab} className='max-h-[min(70vh,44rem)] overflow-y-auto px-6 py-5'>
          {tab === 'diagnosis' ? <DiagnosisPanel report={report} /> : <EvidencePanel report={report} />}
        </div>
      </DialogContent>
    </Dialog>
  );
};
